import * as CookieConsent from "vanilla-cookieconsent";
import {
    applyConsentUpdate,
    CONSENT_COOKIE_EXPIRES_DAYS,
    CONSENT_COOKIE_NAME,
    COOKIE_CATEGORIES,
    FBC_COOKIE,
    fbcCookie,
    marketingConsentCookie,
    OPEN_COOKIE_SETTINGS_EVENT,
    registerCookieSettingsOpener,
    sharedCookieDomain,
} from "./cookieConsent";

const PRIVACY_POLICY_URL = "https://www.orsane.app/privacy-policy#cookies";

let starting: Promise<void> | null = null;
let showPreferences: (() => void) | null = null;

function readCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    if (!match) {
        return null;
    }
    try {
        return decodeURIComponent(match[1]);
    } catch {
        return match[1];
    }
}

function syncSideEffects(accepted: readonly string[]): void {
    const hostname = window.location.hostname;
    const secure = window.location.protocol === "https:";
    // Cookie Store cannot set Domain=.orsane.app. The string is the contract Meta CAPI reads.
    // biome-ignore lint/suspicious/noDocumentCookie: shared parent-domain cookie
    document.cookie = marketingConsentCookie({ accepted, hostname, secure });
    const fbc = fbcCookie({
        accepted,
        search: window.location.search,
        hostname,
        secure,
        current: readCookie(FBC_COOKIE),
        now: Date.now(),
    });
    if (fbc) {
        // biome-ignore lint/suspicious/noDocumentCookie: shared parent-domain cookie
        document.cookie = fbc;
    }
}

function commit(): void {
    const accepted = CookieConsent.getUserPreferences().acceptedCategories;
    applyConsentUpdate(accepted);
    syncSideEffects(accepted);
}

/**
 * Mounts the banner once. A stored decision is replayed into Consent Mode by
 * the inline script in index.html, before this runs; here we only repair the
 * marketing and `_fbc` cookies. A choice made now is announced from `commit`.
 * Never throws into signup or checkout.
 */
export function startCookieConsent(): Promise<void> {
    if (starting) {
        return starting;
    }
    starting = (async () => {
        try {
            if (typeof window === "undefined") {
                return;
            }
            const domain = sharedCookieDomain(window.location.hostname);
            const secure = window.location.protocol === "https:";
            const categorySections = COOKIE_CATEGORIES.map((category) => ({
                title: category.required
                    ? `${category.label} <span class="pm__badge">Always on</span>`
                    : category.label,
                description: category.description,
                linkedCategory: category.id,
            }));

            await CookieConsent.run({
                cookie: {
                    name: CONSENT_COOKIE_NAME,
                    expiresAfterDays: CONSENT_COOKIE_EXPIRES_DAYS,
                    path: "/",
                    sameSite: "Lax",
                    secure,
                    ...(domain ? { domain } : {}),
                },
                guiOptions: {
                    consentModal: {
                        layout: "box",
                        position: "bottom center",
                        equalWeightButtons: false,
                    },
                    preferencesModal: { layout: "box", equalWeightButtons: false },
                },
                categories: {
                    essential: { enabled: true, readOnly: true },
                    preferences: {},
                    statistics: {},
                    marketing: {},
                },
                language: {
                    default: "en",
                    translations: {
                        en: {
                            consentModal: {
                                title: "Cookies",
                                description:
                                    "We use cookies to run Orsane, understand how the site is used, and measure ads. " +
                                    `Essential cookies stay on. Statistics and marketing stay off until you choose. ` +
                                    `<a href="${PRIVACY_POLICY_URL}" class="cc__link">Privacy policy</a>.`,
                                acceptAllBtn: "Accept all",
                                acceptNecessaryBtn: "Refuse",
                                showPreferencesBtn: "Cookie settings",
                            },
                            preferencesModal: {
                                title: "Cookie settings",
                                acceptAllBtn: "Accept all",
                                acceptNecessaryBtn: "Refuse all",
                                savePreferencesBtn: "Save",
                                closeIconLabel: "Close",
                                sections: [
                                    {
                                        description:
                                            "Choose which cookies we may use. Essential cookies stay on — the site does not work without them. " +
                                            "You can change this later from Cookie settings.",
                                    },
                                    ...categorySections,
                                    {
                                        title: "More information",
                                        description:
                                            `The same choice applies on www.orsane.app and my.orsane.app. ` +
                                            `<a href="${PRIVACY_POLICY_URL}" class="cc__link">Privacy policy</a>.`,
                                    },
                                ],
                            },
                        },
                    },
                },
                onFirstConsent: commit,
                onChange: commit,
            });

            if (CookieConsent.validConsent()) {
                syncSideEffects(CookieConsent.getUserPreferences().acceptedCategories);
            }

            showPreferences = () => {
                CookieConsent.showPreferences();
            };
            registerCookieSettingsOpener(showPreferences);
            window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, () => {
                showPreferences?.();
            });
        } catch {
            // A blocked or broken banner must not take down signup or checkout.
        }
    })();
    return starting;
}
